import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { SubGroupRow } from '@/services/groupsManagement/subGroups.types'; // ← add GroupTableRow
import * as React from 'react';
import ManageSubGroupFormContainer from '../../ManageSubGroupFormContainer';
import { useManageSubGroupClubStatus } from '../hooks/useManageSubGroupClubStatus';
import { useUpdateSubGroupStatus } from '../hooks/useUpdateSubGroupStatus';
import { getSubGroupColumns } from './Column';

type SubGroupTableProps = {
  data: SubGroupRow[];
  pagination: Pagination;
  groupId: string;
  defaultGroupDetailValues?: {
    groupName: string;
    groupNameKatakana: string;
  };
  isLoading: boolean;
};

export function SubGroupTable({ data, pagination, groupId, defaultGroupDetailValues, isLoading }: SubGroupTableProps) {
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsync: deleteSubGroup } = useUpdateSubGroupStatus();
  const { mutateAsync: manageSubGroupClubStatus } = useManageSubGroupClubStatus();
  const { t } = useDynamicLocale();
  const { data: locationOptions = [] } = useDropdown('location');

  const columns = React.useMemo(
    () =>
      getSubGroupColumns(
        onOpen,
        groupId,
        openConfirmation,
        deleteSubGroup,
        manageSubGroupClubStatus,
        t,
        locationOptions,
        defaultGroupDetailValues,
      ),
    [
      onOpen,
      groupId,
      defaultGroupDetailValues,
      openConfirmation,
      deleteSubGroup,
      manageSubGroupClubStatus,
      t,
      locationOptions,
    ],
  );

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      title: t('configurationManagement.groupsManagement.manageSubGroups.title'),
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'configurationManagement.groupsManagement.manageSubGroups.title',
      component: <ManageSubGroupFormContainer groupId={groupId} defaultValues={defaultGroupDetailValues} />,
    });
  };

  const canAddNew = useCan({ allOf: [PERMS.group.manage.subgroup] });

  const { table, locationFilter, onLocationFilterChange } = useDataTable({
    data,
    columns,
    pageCount,
    autoResetExpanded: false,
    initialState: {
      //@ts-expect-error dont know why its complaining
      sorting: [{ id: 'subGroupInfo', desc: false }],
    },
    meta: {
      title: t('configurationManagement.groupsManagement.manageSubGroups.pageTitle'),
      actionKey: 'onAddNew',
      action: canAddNew ? onAddNew : undefined,
      totalPage: pagination.totalItems,
      loading: isLoading,
    },
    getRowId: (row) => ('subgroupId' in row ? row.subgroupId : row.clubId),
    getSubRows: (row) => ('subgroupId' in row ? row.subGroupClub : undefined),
    shallow: false,
    clearOnDefault: true,
    statusFilterKey: 'subGroupStatusFilter',
  });

  return (
    <DataTable table={table} className="font-medium">
      <DataTableToolbar locationFilter={locationFilter as string} onLocationFilterChange={onLocationFilterChange} />
    </DataTable>
  );
}
